import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as actions from '../../_redux/actions/InspectionsPageTableActions';
import * as columnFormatters from '../../../_common/column-formatters';
import * as customFormatters from '../column-formatters';
import {useInspectionsPageUIContext} from '../InspectionsPageUIContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid';
import CommonGridSpinner from '../../../_common/components/CommonGridSpinner';
import SortingColumnHeader from "../../../_common/components/SortingColumnHeader/SortingColumnHeader";
import styles from './styles.module.scss';

export function InspectionsPageTable() {
  const {formatMessage} = useIntl();
  const {expandAll, columnsMode} = useInspectionsPageUIContext();
  const [tableRows, setTableRows] = useState([]);
  const generalColumns = useMemo(() => [
    {
      accessor: 'general',
      Header: formatMessage({
        id: 'inspections-column-general', defaultMessage: 'General'
      }),
      width: '100%'
    },
    {
      accessor: 'actions',
      Header: '', width: 90
    },
  ], []);
  const commonColumns = useMemo(() => [
    {
      accessor: 'actions',
      Header: formatMessage({
        id: 'inspections-column-actions', defaultMessage: 'Actions'
      })
    },
    {
      accessor: 'createDateAndStatus',
      Header:
        <SortingColumnHeader
          text={formatMessage({
              id: 'inspections-column-create-date', defaultMessage: 'Create date and status'
            },
            {
              p: (...chunks) => <p>{chunks}</p>,
              br: <br/>
            })}
          fieldName={'createDate'}
          useContext={useInspectionsPageUIContext}
          showDefaultIcon={true}
        />
    },
    {
      accessor: 'firmShumaID',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-firmShumaID', defaultMessage: 'Firm shuma ID'})}
          fieldName={'firmShumaID'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'customers',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-customers', defaultMessage: 'Customers'})}
          fieldName={'customers'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'address',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-address', defaultMessage: 'Address'})}
          fieldName={'address'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'gushHelka',
      Header:
        <SortingColumnHeader
          text={formatMessage({
              id: 'inspections-column-gush-helka', defaultMessage: 'Gush/helka'
            },
            {
              p: (...chunks) => <p>{chunks}</p>,
              br: <br/>
            })}
          fieldName={'gush'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'shumaTypeAndOrigin',
      Header:
        <SortingColumnHeader
          text={formatMessage({
              id: 'inspections-column-shuma-type-and-origin',
              defaultMessage: 'Type and Origin'
            },
            {
              p: (...chunks) => <p>{chunks}</p>,
              br: <br/>
            })}
          fieldName={'shumaType'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'propertyType',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-property-type', defaultMessage: 'Property Type'})}
          fieldName={'propertyType'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'estimatePriceFinal',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-estimate-price', defaultMessage: 'Estimate price'})}
          fieldName={'estimatePriceFinal'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'purchaseCompensation',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-purchaseCompensation', defaultMessage: 'Purchase compensation'})}
          fieldName={'purchaseCompensation'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'monthlyRent',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-monthlyRent', defaultMessage: 'Monthly rent'})}
          fieldName={'monthlyRent'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'registeredArea',
      Header: formatMessage({
        id: 'inspections-column-registeredArea', defaultMessage: 'Registered area'
      })
    },
    {
      accessor: 'roomsCount',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-roomsCount', defaultMessage: 'Rooms count'})}
          fieldName={'roomsCount'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'floor',
      Header: formatMessage({
        id: 'inspections-column-floor', defaultMessage: 'Floor'
      })
    },
    {
      accessor: 'elevator',
      Header: formatMessage({
        id: 'inspections-column-elevator', defaultMessage: 'Elevator'
      })
    },
    {
      accessor: 'userFullName',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-fullName', defaultMessage: 'Full name'})}
          fieldName={'userFullName'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'shumaDueDate',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: 'inspections-column-due-date', defaultMessage: 'Due date'})}
          fieldName={'shumaDueDate'}
          useContext={useInspectionsPageUIContext}
        />
    },
    {
      accessor: 'paid',
      Header: formatMessage({
        id: 'inspections-column-paid', defaultMessage: 'Paid'
      })
    },
  ], []);
  const generalData = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        general: customFormatters.GeneralColumnFormatter(item),
        actions: customFormatters.ActionsColumnFormatter(item),
      };
    })
  ], [tableRows, expandAll]);
  const commonData = useMemo(() => [
    ...tableRows.map((item) => {
      const customersNames = Array.isArray(item.customers) ? item.customers.map(item => {
        const {firstName, lastName} = item;
        return [firstName, lastName].filter(Boolean).join(' ')
      }) : item?.customers ?? '';
      return {
        actions: customFormatters.ActionsColumnFormatter(item, columnsMode),
        createDateAndStatus: customFormatters.CreateAndStatusFormatter(item),
        firmShumaID: columnFormatters.BoldColumnFormatter(item.firmShumaID),
        customers: columnFormatters.SeveralItemsNoRepeatColumnFormatter(customersNames),
        address: customFormatters.AddressFormatter(item.cityName, item.streetName, item.houseNumber),
        gushHelka: customFormatters.GushAndHelkaFormatter(item.gush, item.helka, item.ttHelka),
        shumaTypeAndOrigin: customFormatters.TypeAndOriginFormatter(item.shumaTypeName, item.originTypeName),
        propertyType: columnFormatters.BoldColumnFormatter(item.propertyTypeName),
        estimatePriceFinal: customFormatters.EstimatePriceColumnFormatter(item),
        purchaseCompensation: columnFormatters.BoldColumnFormatter(item.purchaseCompensation, 'xl'),
        monthlyRent: columnFormatters.PriceColumnFormatter(item.monthlyRent),
        registeredArea: columnFormatters.BoldColumnFormatter(item.registeredArea),
        roomsCount: columnFormatters.BoldColumnFormatter(item.roomsCount),
        floor: columnFormatters.BoldColumnFormatter(item.floor),
        elevator: customFormatters.ElevatorColumnFormatter(item.elevator),
        userFullName: columnFormatters.BoldColumnFormatter([item.userFirstName, item.userLastName].filter(Boolean).join(' ')),
        shumaDueDate: columnFormatters.DateColumnFormatter(item.shumaDueDate),
        paid: customFormatters.InspectionOrderPaidColumnFormatter(item),
      };
    })
  ], [tableRows, expandAll, columnsMode]);
  const columns = columnsMode ? commonColumns : generalColumns;
  const data = columnsMode ? commonData : generalData;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({columns, data});

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'inspectionsPageTableReducer',
    UIContext: useInspectionsPageUIContext(),
    getNewEntities: actions.getInspections,
    getNextEntities: actions.getNextInspections,
    onEntitiesChange: entities => {
      entities
        ? setTableRows(entities)
        : setTableRows([]);
    },
    startRequestedPageNumber: 0,
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <CommonGridSpinner/>
    ),
    style: {
      overflow: 'inherit'
    },
  });

  const gridProps = {
    infiniteScrollProps,
    getTableProps,
    headerGroups,
    getTableBodyProps,
    prepareRow,
    rows
  };

  return (
    <CommonGrid
      customHeaderStyles={columnsMode ? styles.customColumnsHeader : styles.customHeader}
      customBodyStyles={columnsMode ? styles.customColumnsBody : styles.customBody}
      {...gridProps}
    />
  );
}

export default InspectionsPageTable;